import React from "react";
import GridHeader from "./GridHeader";
import GridBody from "./GridBody";
import { VisualOverlay } from "../Overlays";
import "./Grid.css";

/**
 * Grid
 * The scrollable wrapper around the table — the freeze-pane sticky
 * positioning all hangs off this element via the wrapperRef.  Renders
 * the column header, the body, and the absolute-positioned visual
 * overlay (images + charts) on top.
 */
/**
 * Publish --sev-inv-zoom so 2px borders/outlines land on a WHOLE number of
 * device pixels. At fractional devicePixelRatios (e.g. Windows 125% scaling,
 * dpr 1.25) a plain 2px border is 2.5 device px: Chrome snaps it down to
 * 2 device px (used value 1.6px) and the fractional edge positions make
 * sides render unevenly - the right/bottom edge can disappear under the
 * neighbouring cell. Selection outlines and the in-cell editor declare
 * calc(2px * var(--sev-inv-zoom, 1)); we set the factor so the result is
 * a crisp >=2 device-pixel line at any dpr and any effective zoom:
 *   devicePx  = max(2, round(2 * dpr * zoom))
 *   cssFactor = devicePx / (2 * dpr * zoom)
 */
function useEffectiveZoomVar(tableRef) {
  React.useEffect(() => {
    const t = tableRef && tableRef.current;
    if (!t) return;
    const apply = () => {
      if (!t.isConnected || !t.offsetWidth) return;
      const zoom = parseFloat(t.style.zoom || "1") || 1;
      const dpr = window.devicePixelRatio || 1;
      const targetDevicePx = Math.max(2, Math.round(2 * dpr * zoom));
      const factor = targetDevicePx / (2 * dpr * zoom);
      if (factor > 0 && isFinite(factor)) {
        t.style.setProperty("--sev-inv-zoom", String(factor));
      }
    };
    apply();
    // resize also fires on browser-zoom / dpr changes
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  });
}

const Grid = React.forwardRef(function Grid({
  wrapperRef, theadRef, tableRef,
  zoomScale, maxCols, colWidths, hiddenCols, hiddenRows,
  frozenCols, frozenColOffsets,
  selectionType, normalizedSelection,
  filters, activeFilterCol, normalizedRows,
  onToggleFilter, onApplyFilter, onCloseFilter,
  onSelectColumn, onExtendColumn, onHeaderContextMenu, onStartResize,
  isLastVisibleBeforeHidden, isLastVisibleBeforeHiddenRow,
  // Body props
  rowHeights, cellStyles, cellRichHtml, cellMerges, cellLinks,
  isRowVisible, selectedCell,
  frozenRows, measuredTheadHeight, frozenRowOffsets,
  onSelectRow, onExtendRow, onStartSelection, onExtendSelection,
  onLinkClick, onCellContextMenu,
  // Overlay
  activeSheet, sheetImages, sheetCharts,
  objectsEditable, copyRange,
  selectedObjectKey, onSelectObject,
  defaultFont,
  numFmts,
  // Virtualization
  rowWindow,
  // Sorting + filter visibility
  getSortState, isFilterColumn,
  onSortAsc, onSortDesc, onClearSort, onCustomSort,
}, _ref) {
  useEffectiveZoomVar(tableRef);

  return (
    <div className="simple-excel-grid-wrapper" ref={wrapperRef}>
      <table
        className="simple-excel-grid"
        style={{
          zoom: zoomScale,
          ...(defaultFont && defaultFont.fontSize ? { "--sev-cell-font-size": defaultFont.fontSize } : null),
          ...(defaultFont && defaultFont.fontFamily ? { "--sev-cell-font-family": defaultFont.fontFamily } : null),
        }}
        ref={tableRef}
      >
        <GridHeader
          ref={theadRef}
          maxCols={maxCols}
          colWidths={colWidths}
          hiddenCols={hiddenCols}
          frozenCols={frozenCols}
          frozenColOffsets={frozenColOffsets}
          selectionType={selectionType}
          normalizedSelection={normalizedSelection}
          isLastVisibleBeforeHidden={isLastVisibleBeforeHidden}
          filters={filters}
          activeFilterCol={activeFilterCol}
          normalizedRows={normalizedRows}
          onToggleFilter={onToggleFilter}
          onApplyFilter={onApplyFilter}
          onCloseFilter={onCloseFilter}
          onSelectColumn={onSelectColumn}
          onExtendColumn={onExtendColumn}
          onHeaderContextMenu={onHeaderContextMenu}
          onStartResize={onStartResize}
          getSortState={getSortState}
          isFilterColumn={isFilterColumn}
          onSortAsc={onSortAsc}
          onSortDesc={onSortDesc}
          onClearSort={onClearSort}
          onCustomSort={onCustomSort}
        />
        <GridBody
          normalizedRows={normalizedRows}
          maxCols={maxCols}
          rowWindow={rowWindow}
          rowHeights={rowHeights}
          colWidths={colWidths}
          cellStyles={cellStyles}
          numFmts={numFmts}
          cellRichHtml={cellRichHtml}
          cellMerges={cellMerges}
          cellLinks={cellLinks}
          hiddenCols={hiddenCols}
          hiddenRows={hiddenRows}
          isRowVisible={isRowVisible}
          isLastVisibleBeforeHidden={isLastVisibleBeforeHidden}
          isLastVisibleBeforeHiddenRow={isLastVisibleBeforeHiddenRow}
          selectedCell={selectedCell}
          normalizedSelection={normalizedSelection}
          selectionType={selectionType}
          frozenRows={frozenRows}
          frozenCols={frozenCols}
          measuredTheadHeight={measuredTheadHeight}
          frozenRowOffsets={frozenRowOffsets}
          frozenColOffsets={frozenColOffsets}
          onSelectRow={onSelectRow}
          onExtendRow={onExtendRow}
          onHeaderContextMenu={onHeaderContextMenu}
          onStartResize={onStartResize}
          onStartSelection={onStartSelection}
          onExtendSelection={onExtendSelection}
          onLinkClick={onLinkClick}
          onCellContextMenu={onCellContextMenu}
          filters={filters}
          activeFilterCol={activeFilterCol}
          isFilterColumn={isFilterColumn}
          getSortState={getSortState}
          onToggleFilter={onToggleFilter}
          onApplyFilter={onApplyFilter}
          onCloseFilter={onCloseFilter}
          onSortAsc={onSortAsc}
          onSortDesc={onSortDesc}
          onClearSort={onClearSort}
          onCustomSort={onCustomSort}
        />
      </table>

      <VisualOverlay
        activeSheet={activeSheet}
        sheetImages={sheetImages}
        sheetCharts={sheetCharts}
        colWidths={colWidths}
        rowHeights={rowHeights}
        zoomScale={zoomScale}
        hiddenCols={hiddenCols}
        hiddenRows={hiddenRows}
        frozenRows={frozenRows}
        frozenCols={frozenCols}
        wrapperRef={wrapperRef}
        objectsEditable={objectsEditable}
        copyRange={copyRange}
        selectedObjectKey={selectedObjectKey}
        onSelectObject={onSelectObject}
      />
    </div>
  );
});

export default Grid;
